import type { LucideIcon } from 'lucide-react';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';

export type InspectorTabListItem<Key extends string = string> = {
  key: Key;
  title: string;
  icon: LucideIcon;
  disabled: boolean;
  disabledMessage?: string;
  show: boolean;
};

export const InspectorTabList = <Key extends string>({
  configuration,
  authIsLoading,
  profileLoading,
}: {
  configuration: Array<InspectorTabListItem<Key>>;
  authIsLoading: boolean;
  profileLoading: boolean;
}) =>
  authIsLoading || profileLoading ? (
    <div
      className="flex w-21.25 max-w-21.25 min-w-21.25 flex-col gap-y-px overflow-y-auto"
      data-cy="inspector-tabs-skeleton"
    >
      {/* 6 is an approximation — real tab count varies ~4-9 depending on feature flags/context */}
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-16.25 max-h-16.25 flex-1" />
      ))}
    </div>
  ) : (
    <TabsList variant="tabs" className="w-21.25 max-w-21.25 min-w-21.25 scrollbar-none overflow-y-auto">
      {configuration
        .filter(({ show }) => show)
        .map((inspectorItem) => (
          <TabsTrigger
            key={inspectorItem.key}
            variant="tabs"
            value={inspectorItem.key}
            data-cy={`${inspectorItem.key}-tab-trigger`}
            icon={inspectorItem.icon}
            disabled={inspectorItem.disabled ?? false}
            disabledMessage={inspectorItem.disabledMessage}
            className="h-16.25 max-h-16.25 flex-1"
          >
            <div className="text-xs">{inspectorItem.title}</div>
          </TabsTrigger>
        ))}

      {/* Hacky solution for coloring the rest of the TabList */}
      <div className="flex-1 bg-ui-base" />
    </TabsList>
  );
